import React, { useState, useEffect, useCallback, useRef } from 'react';
import prodConfig from '../config/prodProductPerformance.json';
import countryCodeMapping from '../config/countryCodeMapping.json';
import { graphqlMutation } from '../utils/graphqlApi';
import { analyzeProdPerformance, generateTribeSummary } from '../graphql/mutations';
import useUserPermissions from '../hooks/useUserPermissions';

const TIME_RANGES = [
  { value: 'current_week', label: 'Current Week' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'past_month', label: 'Past Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
];
// Tribe list aligned with appConfig (Incident Resolver page)
const TRIBES = Array.isArray(prodConfig.tribes) ? prodConfig.tribes : [];

const SOLUTIONS = prodConfig.solutions || {};

// Get service offerings for a tribe from prodConfig
const getOfferingsForTribe = (tribe) => Object.keys(SOLUTIONS[tribe] || {});

// The backend filters incidents on an exact, case-sensitive match against the
// ServiceNow "Service offering" column, so this value is sent verbatim.
const getServiceOfferingValue = (tribe, offeringKey) =>
  SOLUTIONS[tribe]?.[offeringKey] || offeringKey;

const parseJsonSafely = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string') {
      try {
        return JSON.parse(parsed);
      } catch {
        return parsed;
      }
    }
    return parsed;
  } catch (parseError) {
    console.error('[ProdPerformance] Failed to parse JSON payload:', parseError);
    return null;
  }
};

const getAnalysisError = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return payload.errorMessage || payload.message || payload.error || null;
};

// Distinguishes a "the compute is still running" failure from a definitive one.
// The tribe cold path can exceed AppSync's resolver window, which surfaces as a
// thrown timeout/network error while the Lambda keeps computing and warming its
// cache — those are worth polling through. Everything else (unauthorized,
// validation/4xx, malformed request) will never resolve by waiting, so it must
// be surfaced immediately instead of being masked by a 120s "still computing"
// message. graphqlMutation rethrows a plain Error whose message is the first
// GraphQL error string, so we classify on that.
const isRetryableTribeError = (err) => {
  const message = (err?.message || '').toLowerCase();
  if (!message) return false;
  const RETRYABLE_HINTS = [
    'timed out',
    'timeout',
    'network',
    'failed to fetch',
    'econnreset',
    'service unavailable',
    'lambda execution',
    'execution timed out',
  ];
  const DEFINITIVE_HINTS = [
    'unauthorized',
    'not authorized',
    'access denied',
    'forbidden',
    'validation',
    'invalid',
    'bad request',
  ];
  if (DEFINITIVE_HINTS.some((hint) => message.includes(hint))) return false;
  return RETRYABLE_HINTS.some((hint) => message.includes(hint));
};

// Shapes the offering analysis payload into exactly what the drill-down renders.
// The analysis Lambda computes these deterministically in pandas; the payload
// field is still named `bedrock_analysis` because that is the established
// DynamoDB cache schema. Tribe-level aggregates (avg resolve time, SLA
// breaches, recurring counts) come from the tribe summary Lambda, which
// computes them over the whole dataset rather than over the top-5 slice.
const normalizeAnalysis = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nestedAnalysis = payload.bedrock_analysis
    ? parseJsonSafely(payload.bedrock_analysis)
    : null;
  const analysis = nestedAnalysis && typeof nestedAnalysis === 'object' ? nestedAnalysis : payload;

  // Accept either the new top_5_recurring_incidents key or the legacy
  // top_3_recurring_incidents. The deterministic backend emits top_5, but a
  // cached/undeployed backend may still return top_3; falling back keeps the
  // recurring list populated instead of silently rendering empty, so this
  // frontend does not depend on the backend deploy landing first.
  const recurringIncidents = Array.isArray(analysis.top_5_recurring_incidents)
    ? analysis.top_5_recurring_incidents
    : Array.isArray(analysis.top_3_recurring_incidents)
      ? analysis.top_3_recurring_incidents
      : [];

  return {
    total_incidents: analysis.total_incidents ?? 0,
    top_5_countries: Array.isArray(analysis.top_5_countries) ? analysis.top_5_countries : [],
    top_5_recurring_incidents: recurringIncidents,
    top_5_longest_resolve_times: Array.isArray(analysis.top_5_longest_resolve_times)
      ? analysis.top_5_longest_resolve_times
      : [],
    open_onhold_incidents: Array.isArray(analysis.open_onhold_incidents)
      ? analysis.open_onhold_incidents
      : [],
  };
};

// Normalizes a tribe-level analysis response. Tribe-level carries keys the
// offering payload does not: priority_breakdown, avg_resolution_by_priority and
// service_offering_distribution.
const normalizeTribeAnalysis = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nestedAnalysis = payload.bedrock_analysis
    ? parseJsonSafely(payload.bedrock_analysis)
    : null;
  const analysis = nestedAnalysis && typeof nestedAnalysis === 'object' ? nestedAnalysis : payload;

  return {
    total_incidents: analysis.total_incidents ?? 0,
    message: analysis.message || null,
    priority_breakdown: Array.isArray(analysis.priority_breakdown)
      ? analysis.priority_breakdown
      : [],
    avg_resolution_by_priority: Array.isArray(analysis.avg_resolution_by_priority)
      ? analysis.avg_resolution_by_priority
      : [],
    service_offering_distribution: Array.isArray(analysis.service_offering_distribution)
      ? analysis.service_offering_distribution
      : [],
    top_5_recurring_incidents: Array.isArray(analysis.top_5_recurring_incidents)
      ? analysis.top_5_recurring_incidents
      : [],
    top_5_longest_resolve_times: Array.isArray(analysis.top_5_longest_resolve_times)
      ? analysis.top_5_longest_resolve_times
      : [],
    top_5_countries: Array.isArray(analysis.top_5_countries) ? analysis.top_5_countries : [],
    open_onhold_incidents: Array.isArray(analysis.open_onhold_incidents)
      ? analysis.open_onhold_incidents
      : [],
  };
};

// Resolve a ServiceNow location value to a country name.
//
// The analysis Lambda already maps location codes to full country names, but it
// passes through anything its own regex does not recognise (e.g. "IN-Mumbai",
// or a bare "FI"). So only translate values that actually look like a code.
// Blindly reading the first two characters as a code is wrong for full names
// and silently merges distinct countries: "Denmark" would be read as "DE" and
// counted as Germany, "China" as Switzerland, "Ukraine" as United Kingdom,
// "Indonesia" as India, "Austria" as Australia.
//
// The accepted shapes mirror _normalize_country in the analysis Lambda: a bare
// two-letter code, or a two-letter code followed by a non-letter separator.
const resolveCountryName = (rawValue) => {
  const raw = String(rawValue ?? '').trim();
  if (!raw) return '';

  if (/^[A-Za-z]{2}$/.test(raw)) {
    return countryCodeMapping[raw.toUpperCase()] || raw;
  }

  const prefixMatch = raw.match(/^([A-Za-z]{2})[^A-Za-z]/);
  if (prefixMatch) {
    return countryCodeMapping[prefixMatch[1].toUpperCase()] || raw;
  }

  return raw;
};

// Helper to get SLA color based on count
const getSlaColor = (count) => {
  if (count <= 2) return '#10B981'; // green
  if (count <= 7) return '#F59E0B'; // yellow/orange
  return '#EF4444'; // red
};

// Format hours into "X D Y Hrs" format.
//
// Zero is rendered as N/A rather than "0 Mins": the tribe summary Lambda uses 0
// as its sentinel both for "no incidents" and for "no parseable Resolve time
// values", so it never means a genuine zero-hour resolution.
const formatResolutionTime = (hours) => {
  if (hours == null || !Number.isFinite(Number(hours)) || Number(hours) <= 0) return 'N/A';

  // Round to whole minutes up front so carries are handled once. Formatting the
  // components independently used to let 23.999h render as "23 Hrs 60 Mins".
  const totalMinutes = Math.round(Number(hours) * 60);
  if (totalMinutes < 60) return `${totalMinutes} Mins`;

  const days = Math.floor(totalMinutes / 1440);
  const remainingHours = Math.floor((totalMinutes % 1440) / 60);
  const remainingMins = totalMinutes % 60;

  if (days > 0) {
    return remainingHours === 0 ? `${days} D` : `${days} D ${remainingHours} Hrs`;
  }
  return remainingMins === 0
    ? `${remainingHours} Hrs`
    : `${remainingHours} Hrs ${remainingMins} Mins`;
};

function ProdProductPerformance() {
  const {
    canAccessProdPerformance,
    getUserRole,
    isLoading: isPermissionsLoading,
  } = useUserPermissions();

  // --- Section 1: Time Period ---
  const [timeRange, setTimeRange] = useState('current_month');
  const selectedTimeRangeLabel =
    TIME_RANGES.find((tr) => tr.value === timeRange)?.label || timeRange;

  // --- Section 2: Tribe Summary Cards ---
  const [tribeSummaries, setTribeSummaries] = useState({});
  const [isTribeSummaryLoading, setIsTribeSummaryLoading] = useState(false);
  const [tribeSummaryError, setTribeSummaryError] = useState(null);

  // --- Section 3: Drill-Down ---
  const [selectedTribe, setSelectedTribe] = useState('');
  const [selectedOffering, setSelectedOffering] = useState('');
  const [offerings, setOfferings] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);
  const [drillDownError, setDrillDownError] = useState(null);
  // The period the rendered drill-down actually describes. Tracked separately
  // from `timeRange` so the heading cannot claim a period the data is not from.
  const [analyzedContext, setAnalyzedContext] = useState(null);

  // --- Section 3b: Tribe-Level Analysis ---
  const [tribeAnalysisData, setTribeAnalysisData] = useState(null);
  const [isTribeAnalysisLoading, setIsTribeAnalysisLoading] = useState(false);
  const [tribeAnalysisError, setTribeAnalysisError] = useState(null);
  const [tribeAnalysisContext, setTribeAnalysisContext] = useState(null);

  // Monotonic request counters. Neither fetch is cancellable (Amplify's graphql()
  // returns a bare promise), so instead of aborting we drop any response whose
  // id is no longer current. Without this, switching the time period while a
  // request is in flight lets the older response overwrite the newer one and
  // render under the wrong period's label.
  const tribeRequestRef = useRef(0);
  const drillDownRequestRef = useRef(0);
  const tribeAnalysisRequestRef = useRef(0);

  // Fetch tribe summary cards — single API call covering every tribe.
  // generateTribeSummary is both the cached and the compute path: its Lambda
  // returns the DynamoDB-cached summary when one is warm and only recomputes
  // on a miss, reporting which happened via body.source.
  const fetchTribeSummaries = useCallback(async (timeline, { forceRefresh = false } = {}) => {
    const requestId = tribeRequestRef.current + 1;
    tribeRequestRef.current = requestId;
    const isStale = () => tribeRequestRef.current !== requestId;

    setIsTribeSummaryLoading(true);
    setTribeSummaryError(null);
    setTribeSummaries({});

    try {
      const result = await graphqlMutation(generateTribeSummary, {
        input: { time_range: timeline, force_refresh: forceRefresh },
      });
      if (isStale()) return;

      const responseBody = result?.generateTribeSummary?.body;

      if (!responseBody) {
        setTribeSummaryError('No tribe summary data returned.');
        return;
      }

      const parsed = parseJsonSafely(responseBody);
      if (!parsed) {
        setTribeSummaryError('Failed to parse tribe summary response.');
        return;
      }

      // A non-200 statusCode is reported by the Lambda as { status, message }
      const payloadError = getAnalysisError(parsed);
      if (payloadError) {
        setTribeSummaryError(payloadError);
        return;
      }

      // Lambda returns { tribe_summary: { DXI: {...}, FSS: {...}, ... } }
      const tribeSummaryData = parsed.tribe_summary || parsed;

      const summaryMap = {};
      TRIBES.forEach((t) => {
        // A tribe missing from the payload means the backend did not report on
        // it at all — that is a different situation from a tribe that reported
        // zero incidents, and the cards distinguish the two.
        summaryMap[t] = tribeSummaryData[t] ?? { missing: true };
      });
      setTribeSummaries(summaryMap);
    } catch (err) {
      if (isStale()) return;
      console.error('[ProdPerformance] Error fetching tribe summaries:', err);
      setTribeSummaryError(err.message || 'Unable to load tribe metrics.');
    } finally {
      if (!isStale()) setIsTribeSummaryLoading(false);
    }
  }, []);

  // Auto-load tribe summaries on mount and when time range changes.
  //
  // Always force-refresh, for EVERY period. The earlier optimization only
  // force-refreshed open periods on the assumption that closed months read
  // immutable S3 files — but that assumption breaks whenever the source CSVs
  // change (backfill, correction, re-upload) without the cache being
  // invalidated, and the closed-period cache carries a 7-day TTL, so the cards
  // kept showing stale numbers for up to a week. Correctness (always-fresh)
  // wins over the marginal load saving: the compute is fast deterministic
  // pandas, so recomputing on view is cheap.
  useEffect(() => {
    if (!timeRange) return;

    fetchTribeSummaries(timeRange, { forceRefresh: true });

    // Abandon any in-flight drill-down: its result describes the previous
    // period and must not be rendered under the new one.
    drillDownRequestRef.current += 1;
    setIsDrillDownLoading(false);
    setPerformanceData(null);
    setAnalyzedContext(null);
    setDrillDownError(null);

    // Also reset tribe-level analysis
    tribeAnalysisRequestRef.current += 1;
    setIsTribeAnalysisLoading(false);
    setTribeAnalysisData(null);
    setTribeAnalysisContext(null);
    setTribeAnalysisError(null);

    // Tribe and offering selection are deliberately kept. They are filter
    // inputs, not results, so clearing them forced the user to re-pick from a
    // 30-item dropdown just to compare the same offering across two periods.
  }, [timeRange, fetchTribeSummaries]);

  // Update offerings list when tribe changes
  useEffect(() => {
    setOfferings(selectedTribe ? getOfferingsForTribe(selectedTribe) : []);
    setSelectedOffering('');
    // A different tribe means a different offering, so any rendered result and
    // any in-flight request no longer apply.
    drillDownRequestRef.current += 1;
    setIsDrillDownLoading(false);
    setPerformanceData(null);
    setAnalyzedContext(null);
    setDrillDownError(null);

    // Also reset tribe-level analysis when tribe changes
    tribeAnalysisRequestRef.current += 1;
    setIsTribeAnalysisLoading(false);
    setTribeAnalysisData(null);
    setTribeAnalysisContext(null);
    setTribeAnalysisError(null);
  }, [selectedTribe]);

  // Fetch drill-down analysis for the selected service offering.
  // The tribe-wide equivalent is handleAnalyzeTribe below; both call
  // analyzeProdPerformance, differing only in which parameter they send.
  const handleAnalyze = async ({ forceRefresh = false } = {}) => {
    if (!selectedTribe || !selectedOffering || !timeRange) {
      setDrillDownError('Please select a Tribe, a Service Offering and a Time Period.');
      return;
    }

    const requestId = drillDownRequestRef.current + 1;
    drillDownRequestRef.current = requestId;
    const isStale = () => drillDownRequestRef.current !== requestId;

    // Captured up front so the result is labelled with the filters it was
    // actually requested with, even if the user changes them mid-flight.
    const context = {
      tribe: selectedTribe,
      offering: selectedOffering,
      timeRange,
      timeRangeLabel: selectedTimeRangeLabel,
    };

    setIsDrillDownLoading(true);
    setDrillDownError(null);
    setPerformanceData(null);
    setAnalyzedContext(null);

    // Clear tribe analysis — only one result should be visible at a time
    setTribeAnalysisData(null);
    setTribeAnalysisContext(null);
    setTribeAnalysisError(null);

    const serviceOffering = getServiceOfferingValue(selectedTribe, selectedOffering);

    // Single round-trip that classifies the outcome, mirroring the tribe path:
    //   'empty'   — resolver returned no body (AppSync's ~30s window elapsed
    //               while the Lambda kept computing and warming its DynamoDB
    //               cache). Retained as a safety net: the analysis is now
    //               deterministic pandas and returns well inside the window, so
    //               this path should not normally be taken.
    //   'error'   — a definitive error to surface immediately (no retry).
    //   'success' — normalized data ready to render.
    //
    // `refresh` is only honoured on the first attempt; every poll after that
    // reads the warming cache (force_refresh: false), otherwise each poll would
    // kick off a brand-new compute and the cache would never settle.
    const runDrillDownAnalysis = async (refresh) => {
      let result;
      try {
        result = await graphqlMutation(analyzeProdPerformance, {
          input: {
            // Send the tribe too: the offering is chosen from within a tribe, so
            // the backend scopes the drill-down to this tribe and its total then
            // matches that offering's bar in the tribe card. Presence of
            // service_offering still selects offering mode on the backend.
            tribe: selectedTribe,
            service_offering: serviceOffering,
            time_range: timeRange,
            force_refresh: refresh,
          },
        });
      } catch (err) {
        // Only timeout/network failures mean "still computing" — poll through
        // those. Definitive failures (auth, validation, 4xx) never resolve by
        // waiting, so surface them immediately.
        if (isRetryableTribeError(err)) {
          console.warn('[ProdPerformance] Drill-down timed out, will retry:', err);
          return { outcome: 'empty' };
        }
        console.error('[ProdPerformance] Drill-down failed (not retrying):', err);
        return { outcome: 'error', error: err?.message || 'Failed to fetch performance data.' };
      }

      const rawResponse = result?.analyzeProdPerformance;
      const responseBody = rawResponse?.body;
      const statusCode = rawResponse?.statusCode;

      if (!responseBody && !statusCode) {
        console.warn('[ProdPerformance] Drill-down returned empty response:', result);
        return { outcome: 'empty' };
      }

      if (statusCode && statusCode >= 400) {
        const errorParsed = parseJsonSafely(responseBody);
        const errorMsg =
          errorParsed?.message ||
          errorParsed?.body?.message ||
          `Request failed with status ${statusCode}`;
        return { outcome: 'error', error: errorMsg };
      }

      const parsed = parseJsonSafely(responseBody);
      const analysisError = getAnalysisError(parsed);
      if (analysisError) {
        return { outcome: 'error', error: analysisError };
      }

      const normalized = normalizeAnalysis(parsed);
      if (!normalized) {
        return { outcome: 'error', error: 'No usable performance data returned.' };
      }

      return { outcome: 'success', normalized, source: parsed.source || null };
    };

    try {
      // Poll for the cold-path result, reading the warming cache on a fixed
      // interval up to a ceiling. Only an 'empty' outcome is retried; errors and successes
      // are final.
      //
      // The ceiling is deliberately short. Each retry is a fresh synchronous
      // invocation that re-reads S3, so a long ceiling fans out many duplicate
      // full computes per click. Now that the analysis is deterministic pandas
      // and returns inside the resolver window, this loop is a safety net for
      // an unusually slow cold path, not the normal route.
      const POLL_INTERVAL_MS = 5000;
      const MAX_WAIT_MS = 30000;
      const deadline = Date.now() + MAX_WAIT_MS;
      let attempt = 0;
      let response;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Honour the caller's force_refresh only on the first attempt.
        response = await runDrillDownAnalysis(attempt === 0 ? forceRefresh : false);
        attempt += 1;
        if (isStale()) return;

        if (response.outcome !== 'empty' || Date.now() >= deadline) break;

        // Wait for the backend to make progress before polling the cache again.
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (isStale()) return;
      }

      if (response.outcome === 'success') {
        setPerformanceData(response.normalized);
        setAnalyzedContext({ ...context, source: response.source });
      } else if (response.outcome === 'error') {
        setDrillDownError(response.error);
      } else {
        setDrillDownError(
          'Analysis is taking longer than expected. It is still being computed in the background — please try again in a minute.'
        );
      }
    } catch (err) {
      if (isStale()) return;
      console.error('[ProdPerformance] Error fetching drill-down:', err);
      setDrillDownError(err.message || 'Failed to fetch performance data.');
    } finally {
      if (!isStale()) setIsDrillDownLoading(false);
    }
  };

  // Fetch tribe-level analysis — calls analyzeProdPerformance with just the tribe param.
  const handleAnalyzeTribe = async ({ forceRefresh = false } = {}) => {
    if (!selectedTribe || !timeRange) {
      setTribeAnalysisError('Please select a Tribe and a Time Period.');
      return;
    }

    const requestId = tribeAnalysisRequestRef.current + 1;
    tribeAnalysisRequestRef.current = requestId;
    const isStale = () => tribeAnalysisRequestRef.current !== requestId;

    const context = {
      tribe: selectedTribe,
      timeRange,
      timeRangeLabel: selectedTimeRangeLabel,
    };

    setIsTribeAnalysisLoading(true);
    setTribeAnalysisError(null);
    setTribeAnalysisData(null);
    setTribeAnalysisContext(null);

    // Clear offering analysis — only one result should be visible at a time
    setPerformanceData(null);
    setAnalyzedContext(null);
    setDrillDownError(null);

    // Performs a single round-trip and classifies the outcome:
    //   'empty'   — resolver returned no body and no status. The tribe-wide
    //               analysis spans every service offering.
    //               The resolver's response window is far shorter, so the cold
    //               path returns empty while the Lambda keeps computing and
    //               warms its DynamoDB cache. We poll until the cache is warm.
    //   'error'   — a definitive error to surface immediately (no retry). This
    //               includes thrown auth/validation/4xx failures, which will
    //               never resolve by waiting; only timeout/network errors are
    //               reclassified as 'empty' and polled through.
    //   'success' — normalized data ready to render.
    //
    // `refresh` is only honoured on the first attempt. Every poll after that
    // reads the warming cache (force_refresh: false); otherwise each poll would
    // kick off a brand-new compute and the cache would never settle.
    const runTribeAnalysis = async (refresh) => {
      let result;
      try {
        result = await graphqlMutation(analyzeProdPerformance, {
          input: {
            tribe: selectedTribe,
            time_range: timeRange,
            force_refresh: refresh,
          },
        });
      } catch (err) {
        // Only timeout/network failures mean "still computing" — poll through
        // those. Definitive failures (auth, validation, 4xx) will never resolve
        // by waiting, so surface them immediately rather than masking them
        // behind a 120s "still computing" message.
        if (isRetryableTribeError(err)) {
          console.warn('[ProdPerformance] Tribe analysis timed out, will retry:', err);
          return { outcome: 'empty' };
        }
        console.error('[ProdPerformance] Tribe analysis failed (not retrying):', err);
        return { outcome: 'error', error: err?.message || 'Failed to fetch tribe analysis.' };
      }

      const rawResponse = result?.analyzeProdPerformance;
      const responseBody = rawResponse?.body;
      const statusCode = rawResponse?.statusCode;

      if (!responseBody && !statusCode) {
        console.warn('[ProdPerformance] Tribe analysis returned empty response:', result);
        return { outcome: 'empty' };
      }

      if (statusCode && statusCode >= 400) {
        const errorParsed = parseJsonSafely(responseBody);
        const errorMsg =
          errorParsed?.message ||
          errorParsed?.body?.message ||
          `Request failed with status ${statusCode}`;
        return { outcome: 'error', error: errorMsg };
      }

      const parsed = parseJsonSafely(responseBody);
      const analysisError = getAnalysisError(parsed);
      if (analysisError) {
        return { outcome: 'error', error: analysisError };
      }

      const normalized = normalizeTribeAnalysis(parsed);
      if (!normalized) {
        return { outcome: 'error', error: 'No usable tribe analysis data returned.' };
      }

      return { outcome: 'success', normalized, source: parsed.source || null };
    };

    try {
      // Poll for the cold-path result, reading the cache on a fixed interval up
      // to a ceiling. Only an 'empty' outcome is retried; errors and successes
      // are final. Ceiling kept short for the same reason as the drill-down
      // loop: each retry is another full synchronous compute, not a cheap read.
      const POLL_INTERVAL_MS = 5000;
      const MAX_WAIT_MS = 30000;
      const deadline = Date.now() + MAX_WAIT_MS;
      let attempt = 0;
      let response;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Honour the caller's force_refresh only on the first attempt.
        response = await runTribeAnalysis(attempt === 0 ? forceRefresh : false);
        attempt += 1;
        if (isStale()) return;

        if (response.outcome !== 'empty' || Date.now() >= deadline) break;

        // Wait for the backend to make progress before polling the cache again.
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (isStale()) return;
      }

      if (response.outcome === 'success') {
        setTribeAnalysisData(response.normalized);
        setTribeAnalysisContext({ ...context, source: response.source });
      } else if (response.outcome === 'error') {
        setTribeAnalysisError(response.error);
      } else {
        setTribeAnalysisError(
          'Tribe analysis is taking longer than expected. It is still being computed in the background — please try again in a minute.'
        );
      }
    } catch (err) {
      if (isStale()) return;
      console.error('[ProdPerformance] Error fetching tribe analysis:', err);
      setTribeAnalysisError(err.message || 'Failed to fetch tribe analysis.');
    } finally {
      if (!isStale()) setIsTribeAnalysisLoading(false);
    }
  };

  // --- Render: Tribe Summary Cards ---
  const renderTribeSummaryCards = () => {
    if (isTribeSummaryLoading) {
      return (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Tribe Performance Overview</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {TRIBES.map((t) => (
                <div key={t} className="col-lg-4 col-md-6 mb-3">
                  <div className="card h-100" style={{ borderRadius: '12px' }}>
                    <div className="card-body">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6 mb-2"></span>
                        <span className="placeholder col-8"></span>
                        <span className="placeholder col-5"></span>
                        <span className="placeholder col-7"></span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (tribeSummaryError) {
      return (
        <div
          className="alert alert-danger d-flex align-items-center justify-content-between"
          role="alert"
        >
          <span>{tribeSummaryError}</span>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => fetchTribeSummaries(timeRange, { forceRefresh: true })}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="card mb-4">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h5 className="mb-0">Tribe Performance Overview</h5>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-dark border">{selectedTimeRangeLabel}</span>
            {/* Forces a recompute: force_refresh makes the Lambda skip its cache
                read and re-aggregate from the current ServiceNow export. */}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => fetchTribeSummaries(timeRange, { forceRefresh: true })}
              disabled={isTribeSummaryLoading}
              title="Recompute tribe metrics from the latest ServiceNow export, ignoring any cached result."
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            {TRIBES.map((t) => {
              const summary = tribeSummaries[t];

              // Three distinct states, previously collapsed into one:
              //   notReported — the backend returned no entry for this tribe
              //   noIncidents — the tribe reported zero incidents in the period
              //   populated   — real metrics
              const notReported = !summary || summary.missing || summary.error;
              const totalIncidents = notReported ? null : (summary.total_incidents ?? 0);
              const noIncidents = totalIncidents === 0;
              const isSelected = selectedTribe === t;

              const slaBreach = notReported ? 0 : (summary.sla_breached_count ?? 0);

              return (
                <div key={t} className="col-lg-4 col-md-6 mb-3">
                  <div
                    className="card h-100"
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    aria-label={`Select ${t} for drill-down analysis`}
                    onClick={() => setSelectedTribe(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedTribe(t);
                      }
                    }}
                    style={{
                      borderRadius: '12px',
                      border: `1px solid ${isSelected ? '#1C64F2' : '#E5E7EB'}`,
                      backgroundColor: isSelected ? '#F5F8FF' : '#FFFFFF',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="card-body">
                      <h6 className="card-title fw-bold mb-3" style={{ fontSize: '1.1rem' }}>
                        {t}
                      </h6>
                      {notReported && (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                          No metrics returned for this tribe. The incident export for this period
                          may not cover its assignment groups yet.
                        </p>
                      )}
                      {!notReported && noIncidents && (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                          No incidents recorded in this period.
                        </p>
                      )}
                      {!notReported && !noIncidents && (
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-bar-chart-fill text-primary"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                              {totalIncidents}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              Incidents Reported
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-clock text-info"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                              {formatResolutionTime(summary.avg_resolution_time_hours)}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: '0.8rem' }}
                              title="Mean across incidents with a recorded resolve time. Open and unresolved incidents are excluded from this average but still counted in Incidents Reported."
                            >
                              Average Resolve Time
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="bi bi-exclamation-triangle-fill"
                              style={{ color: getSlaColor(slaBreach) }}
                            ></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                              {slaBreach}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              SLAs Breached (Count of Incidents)
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-arrow-repeat text-secondary"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                              {summary.recurring_incident_count ?? 0}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: '0.8rem' }}
                              title="Number of distinct incident descriptions that occurred more than once in this period — not the total count of repeat incidents."
                            >
                              Recurring Incidents
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-collection text-success"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                              {getOfferingsForTribe(t).length}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: '0.8rem' }}
                              title="Number of service offerings configured for this tribe. This is a static configuration count and does not change with the selected time period."
                            >
                              Service Offerings
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
            Select a tribe card to load it into the drill-down below.
          </p>
        </div>
      </div>
    );
  };

  // --- Render: Tribe-Level Analysis Detail ---
  const renderTribeAnalysisDetail = () => {
    if (!tribeAnalysisData || !tribeAnalysisContext) return null;

    const {
      total_incidents,
      message,
      priority_breakdown,
      avg_resolution_by_priority,
      service_offering_distribution,
      top_5_recurring_incidents,
      top_5_longest_resolve_times,
      top_5_countries,
      open_onhold_incidents,
    } = tribeAnalysisData;

    // Handle empty data (total_incidents: 0 with a message from backend)
    const hasNoData = total_incidents === 0;

    // Country aggregation for top_5_countries
    const countryAggregation = {};
    top_5_countries.forEach((item) => {
      const name = resolveCountryName(item.country || item.name || '');
      if (!name) return;
      const count = item.incident_count || item.count || item.incidents || item.total || 0;
      countryAggregation[name] = (countryAggregation[name] || 0) + count;
    });
    const combinedCountries = Object.entries(countryAggregation)
      .map(([name, count]) => ({ country: name, incident_count: count }))
      .sort((a, b) => b.incident_count - a.incident_count)
      .slice(0, 5);
    const maxCountryCount = combinedCountries.length
      ? Math.max(...combinedCountries.map((c) => c.incident_count))
      : 0;

    return (
      <div className="card mb-4">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h5 className="mb-0">
            {tribeAnalysisContext.tribe} — Tribe Analysis
            <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
              {' '}
              · {tribeAnalysisContext.timeRangeLabel}
            </span>
          </h5>
          {/* Always available (not gated on cache state, so it reveals nothing
              about whether this result was cached or freshly computed) — forces
              a fresh re-run via force_refresh. */}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleAnalyzeTribe({ forceRefresh: true })}
            disabled={isTribeAnalysisLoading}
            title="Re-run this tribe analysis against the latest ServiceNow export."
          >
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            Refresh
          </button>
        </div>
        <div className="card-body">
          {hasNoData && (
            <div className="alert alert-warning" role="alert">
              <strong>No incidents found.</strong>
              <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
                {message ||
                  `No incidents found for tribe '${tribeAnalysisContext.tribe}' in ${tribeAnalysisContext.timeRangeLabel}.`}
              </p>
            </div>
          )}

          {!hasNoData && (
            <>
              {/* KPI Cards */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div
                    className="card text-white text-center h-100"
                    style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
                  >
                    <div className="card-body py-3 d-flex flex-column justify-content-center">
                      <h6 className="card-title mb-1" style={{ fontSize: '0.85rem' }}>
                        Total Incidents
                      </h6>
                      <div style={{ fontSize: '2.2rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                        {total_incidents}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="card text-white text-center h-100"
                    style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
                  >
                    <div className="card-body py-3 d-flex flex-column justify-content-center">
                      <h6 className="card-title mb-1" style={{ fontSize: '0.85rem' }}>
                        Top Country
                      </h6>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                        {combinedCountries[0]?.country || '—'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="card text-white text-center h-100"
                    style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
                  >
                    <div className="card-body py-3 d-flex flex-column justify-content-center">
                      <h6 className="card-title mb-1" style={{ fontSize: '0.85rem' }}>
                        Open / On Hold
                      </h6>
                      <div style={{ fontSize: '2.2rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                        {open_onhold_incidents.length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="card text-white text-center h-100"
                    style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
                  >
                    <div className="card-body py-3 d-flex flex-column justify-content-center">
                      <h6 className="card-title mb-1" style={{ fontSize: '0.85rem' }}>
                        Recurring Incidents
                      </h6>
                      <div style={{ fontSize: '2.2rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                        {top_5_recurring_incidents.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Priority Breakdown + Avg Resolution by Priority */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0">Priority Breakdown</h6>
                    </div>
                    <div className="card-body">
                      {priority_breakdown.length === 0 && (
                        <p className="text-muted mb-0">No priority data available.</p>
                      )}
                      {priority_breakdown.map((item, idx) => (
                        <div key={idx} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>{item.priority}</strong>
                            <span>{item.count}</span>
                          </div>
                          <div
                            style={{ background: '#E5E7EB', borderRadius: '4px', height: '22px' }}
                          >
                            <div
                              style={{
                                width: `${total_incidents > 0 ? (item.count / total_incidents) * 100 : 0}%`,
                                background: item.priority?.includes('Critical')
                                  ? '#DC2626'
                                  : item.priority?.includes('High')
                                    ? '#F59E0B'
                                    : item.priority?.includes('Moderate')
                                      ? '#3B82F6'
                                      : '#10B981',
                                height: '100%',
                                borderRadius: '4px',
                                minWidth: '20px',
                                transition: 'width 0.4s ease',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0">Average Resolution Time by Priority</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Priority</th>
                              <th>Average Resolve Time</th>
                              <th title="From ServiceNow's made_sla flag. N/A where ServiceNow has not evaluated the SLA.">
                                SLA Met
                              </th>
                              <th>Breached (count)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {avg_resolution_by_priority.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-3">
                                  No resolution time data available.
                                </td>
                              </tr>
                            )}
                            {avg_resolution_by_priority.map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong>{item.priority}</strong>
                                </td>
                                <td>{item.avg_resolve_time || '—'}</td>
                                <td>
                                  {item.sla_met == null ? (
                                    <span className="badge bg-secondary">N/A</span>
                                  ) : item.sla_met ? (
                                    <span className="badge bg-success">Met</span>
                                  ) : (
                                    <span className="badge bg-danger">Breached</span>
                                  )}
                                </td>
                                <td>{item.sla_breached_count ?? 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Offering Distribution */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Incidents by Service Offering</h6>
                    </div>
                    <div className="card-body">
                      {service_offering_distribution.length === 0 && (
                        <p className="text-muted mb-0">
                          No service offering distribution data available.
                        </p>
                      )}
                      {service_offering_distribution.map((item, idx) => (
                        <div key={idx} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>{item.service_offering}</strong>
                            <span>{item.count}</span>
                          </div>
                          <div
                            style={{ background: '#E5E7EB', borderRadius: '4px', height: '22px' }}
                          >
                            <div
                              style={{
                                width: `${total_incidents > 0 ? (item.count / total_incidents) * 100 : 0}%`,
                                background: '#1C64F2',
                                height: '100%',
                                borderRadius: '4px',
                                minWidth: '20px',
                                transition: 'width 0.4s ease',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top 5 Countries + Top 5 Recurring Incidents */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0">Top 5 Countries by Incident Count</h6>
                    </div>
                    <div className="card-body">
                      {combinedCountries.length === 0 && (
                        <p className="text-muted mb-0">No country data available.</p>
                      )}
                      {combinedCountries.map((item) => (
                        <div key={item.country} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>{item.country}</strong>
                            <span>{item.incident_count}</span>
                          </div>
                          <div
                            style={{ background: '#E5E7EB', borderRadius: '4px', height: '22px' }}
                          >
                            <div
                              style={{
                                width: `${maxCountryCount > 0 ? (item.incident_count / maxCountryCount) * 100 : 0}%`,
                                background: '#1C64F2',
                                height: '100%',
                                borderRadius: '4px',
                                minWidth: '20px',
                                transition: 'width 0.4s ease',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0">Top 5 Recurring Incidents</h6>
                    </div>
                    <div className="card-body">
                      <ul className="list-group list-group-flush">
                        {top_5_recurring_incidents.length === 0 && (
                          <li className="list-group-item text-muted">
                            There are no recurring incidents at the moment.
                          </li>
                        )}
                        {top_5_recurring_incidents.map((item, idx) => (
                          <li key={idx} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                              <strong>{item.incident_type || item.type || '—'}</strong>
                              <span className="badge ms-2" style={{ backgroundColor: '#DC2626' }}>
                                {item.count ?? item.frequency ?? 0}
                              </span>
                            </div>
                            {/* Tribe analysis intentionally omits per-incident
                                numbers entirely — they are shown only on the
                                offering analysis. */}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top 5 Longest Resolution Times */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Top 5 Longest Resolution Times</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Incident Number</th>
                              <th>Resolve Time</th>
                              <th>Short Description</th>
                              <th>Priority</th>
                              <th title="From ServiceNow's made_sla flag. N/A where ServiceNow has not evaluated the SLA.">
                                SLA Met
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {top_5_longest_resolve_times.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center text-muted py-3">
                                  Nothing resolved in this period yet.
                                </td>
                              </tr>
                            )}
                            {top_5_longest_resolve_times.map((item, idx) => (
                              <tr key={item.incident_number || idx}>
                                <td>
                                  <strong>{item.incident_number || '—'}</strong>
                                </td>
                                <td>{item.resolve_time || '—'}</td>
                                <td>{item.short_description || '—'}</td>
                                <td>{item.priority || 'N/A'}</td>
                                <td>
                                  {item.sla_breached == null ? (
                                    <span className="badge bg-secondary">N/A</span>
                                  ) : item.sla_breached ? (
                                    <span className="badge bg-danger">Breached</span>
                                  ) : (
                                    <span className="badge bg-success">Met</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Still Open / On Hold */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">Still Open / On Hold Incidents</h6>
                      <span className="badge bg-secondary">{open_onhold_incidents.length}</span>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Incident Number</th>
                              <th>Short Description</th>
                              <th>Priority</th>
                              <th>Impact</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {open_onhold_incidents.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center text-muted py-3">
                                  There are no Open Incidents at the moment.
                                </td>
                              </tr>
                            )}
                            {open_onhold_incidents.map((item, idx) => (
                              <tr key={item.incident_number || idx}>
                                <td>
                                  <strong>{item.incident_number || '—'}</strong>
                                </td>
                                <td>{item.short_description || '—'}</td>
                                <td>{item.priority || 'N/A'}</td>
                                <td>{item.impact || 'N/A'}</td>
                                <td>{item.status || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // --- Render: Drill-Down Detail ---
  const renderDrillDownDetail = () => {
    if (!performanceData || !analyzedContext) return null;

    const totalIncidents = performanceData?.total_incidents ?? 0;
    const topCountries = Array.isArray(performanceData?.top_5_countries)
      ? performanceData.top_5_countries
      : [];
    const recurringIncidents = Array.isArray(performanceData?.top_5_recurring_incidents)
      ? performanceData.top_5_recurring_incidents
      : [];
    const longestResolveTimes = Array.isArray(performanceData?.top_5_longest_resolve_times)
      ? performanceData.top_5_longest_resolve_times
      : [];
    const openOnHold = Array.isArray(performanceData?.open_onhold_incidents)
      ? performanceData.open_onhold_incidents
      : [];
    // Aggregate countries by name. The API may return a full country name, a
    // bare country code, or a region-prefixed value such as "IN-Mumbai".
    const countryAggregation = {};
    topCountries.forEach((item) => {
      const name = resolveCountryName(item.country || item.name || '');
      if (!name) return;
      // incident_count may come as different field names
      const count = item.incident_count || item.count || item.incidents || item.total || 0;
      countryAggregation[name] = (countryAggregation[name] || 0) + count;
    });
    const combinedCountries = Object.entries(countryAggregation)
      .map(([name, count]) => ({ country: name, incident_count: count }))
      .sort((a, b) => b.incident_count - a.incident_count)
      .slice(0, 5);
    const maxCountryCount = combinedCountries.length
      ? Math.max(...combinedCountries.map((c) => c.incident_count))
      : 0;

    // The backend returns a successful, cacheable 200 with zeroes when the
    // service offering string matches no rows in the ServiceNow export. That is
    // indistinguishable from a genuinely quiet offering unless it is called out,
    // and the empty result gets cached, so surface it explicitly.
    const hasNoData =
      totalIncidents === 0 &&
      combinedCountries.length === 0 &&
      recurringIncidents.length === 0 &&
      longestResolveTimes.length === 0 &&
      openOnHold.length === 0;

    return (
      <>
        {/* Context for the numbers below — from the request, not the live filters */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h5 className="mb-0">
            {analyzedContext.offering}
            <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
              {' '}
              — {analyzedContext.tribe} · {analyzedContext.timeRangeLabel}
            </span>
          </h5>
          {/* Always available (not gated on cache state, so it reveals nothing
              about whether this result was cached or freshly computed) — forces
              a fresh re-run via force_refresh. */}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleAnalyze({ forceRefresh: true })}
            disabled={isDrillDownLoading}
            title="Re-run this analysis against the latest ServiceNow export."
          >
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            Refresh
          </button>
        </div>

        {hasNoData && (
          <div className="alert alert-warning" role="alert">
            <strong>No incidents matched this service offering.</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              The analysis ran successfully but returned nothing for{' '}
              <strong>{analyzedContext.offering}</strong> in {analyzedContext.timeRangeLabel}.
              Either no incidents were raised, or the offering name in ServiceNow no longer matches
              the one configured here — the backend matches it exactly, including case and spacing.
            </p>
          </div>
        )}

        {/* KPI Cards */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div
              className="card text-white text-center h-100"
              style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
            >
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <h6 className="card-title mb-1">Total Incidents</h6>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                  {totalIncidents}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card text-white text-center h-100"
              style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
            >
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <h6 className="card-title mb-1">Top Country</h6>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {combinedCountries[0]?.country || '—'}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card text-white text-center h-100"
              style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}
            >
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <a
                  href="https://app.powerbi.com/groups/me/reports/e268437e-e7a5-42e3-a16f-ea105d757161/ad177848efe0fa9016b2?experience=power-bi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kone-btn kone-btn-white"
                >
                  View Ops In-Depth Analysis
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Countries + Top 5 Recurring */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header">
                <h6 className="mb-0">Top 5 Countries by Incident Count</h6>
              </div>
              <div className="card-body">
                {combinedCountries.length === 0 && (
                  <p className="text-muted mb-0">
                    No country data available for the selected period.
                  </p>
                )}
                {combinedCountries.map((item) => (
                  <div key={item.country} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <strong>{item.country}</strong>
                      <span>{item.incident_count}</span>
                    </div>
                    <div style={{ background: '#E5E7EB', borderRadius: '4px', height: '22px' }}>
                      <div
                        style={{
                          width: `${maxCountryCount > 0 ? (item.incident_count / maxCountryCount) * 100 : 0}%`,
                          background: '#1C64F2',
                          height: '100%',
                          borderRadius: '4px',
                          minWidth: '20px',
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header">
                <h6 className="mb-0">Top 5 Recurring Incidents</h6>
              </div>
              <div className="card-body">
                <ul className="list-group list-group-flush">
                  {recurringIncidents.length === 0 && (
                    <li className="list-group-item text-muted">
                      There are no recurring incidents at the moment.
                    </li>
                  )}
                  {recurringIncidents.map((item, idx) => (
                    <li key={idx} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <strong>{item.type || item.incident_type || '—'}</strong>
                        <span className="badge ms-2" style={{ backgroundColor: '#DC2626' }}>
                          {item.count ?? item.frequency ?? 0}
                        </span>
                      </div>
                      {Array.isArray(item.incident_numbers) && item.incident_numbers.length > 0 ? (
                        <div className="mt-1">
                          <small className="text-muted d-block mb-1">
                            <strong>Incidents</strong> ({item.incident_numbers.length})
                          </small>
                          {/* Render as individual chips in a wrapping, scrollable
                              box. A raw comma-join became an unreadable wall of
                              text for high-volume offerings (hundreds of numbers);
                              chips + a capped-height scroll keep it legible at any
                              volume without a show-more toggle. */}
                          <div
                            className="d-flex flex-wrap gap-1"
                            style={{ maxHeight: '160px', overflowY: 'auto' }}
                          >
                            {item.incident_numbers.map((num) => (
                              <span
                                key={num}
                                className="badge bg-light text-dark border"
                                style={{ fontWeight: 'normal', fontSize: '0.75rem' }}
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <small className="text-muted d-block mt-1">
                          <strong>Incidents:</strong> N/A
                        </small>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Longest Resolve Times */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Top 5 Longest Resolution Times</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Incident Number</th>
                        <th>Resolve Time</th>
                        <th>Short Description</th>
                        <th>Priority</th>
                        <th title="From ServiceNow's made_sla flag. N/A where ServiceNow has not evaluated the SLA.">
                          SLA Met
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {longestResolveTimes.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-3">
                            Nothing resolved in this period yet.
                          </td>
                        </tr>
                      )}
                      {longestResolveTimes.map((item, idx) => {
                        const incNumber = item.incident_number || item.number;
                        let shortDesc = item.short_description || item.description;
                        if (!shortDesc && incNumber) {
                          // Fall back to the recurring group this incident belongs
                          // to. The backend contract for these entries is
                          // `incident_numbers` + `incident_type`; the previous
                          // lookup used `incidents`/`description`, which the
                          // analysis payload never contains, so it never resolved.
                          const match = recurringIncidents.find(
                            (ri) =>
                              Array.isArray(ri.incident_numbers) &&
                              ri.incident_numbers.includes(incNumber)
                          );
                          if (match) shortDesc = match.incident_type || match.type;
                        }
                        return (
                          <tr key={incNumber || idx}>
                            <td>
                              <strong>{incNumber || '—'}</strong>
                            </td>
                            <td>{item.resolve_time || '—'}</td>
                            <td>{shortDesc || '—'}</td>
                            <td>{item.priority || 'N/A'}</td>
                            <td>
                              {item.sla_breached == null ? (
                                <span className="badge bg-secondary">N/A</span>
                              ) : item.sla_breached ? (
                                <span className="badge bg-danger">Breached</span>
                              ) : (
                                <span className="badge bg-success">Met</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Still Open / On Hold — already part of the analysis payload */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Still Open / On Hold</h6>
                <span className="badge bg-secondary">{openOnHold.length}</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Incident Number</th>
                        <th>Short Description</th>
                        <th>Impact</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openOnHold.length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">
                            There are no Open Incidents at the moment.
                          </td>
                        </tr>
                      )}
                      {openOnHold.map((item, idx) => {
                        const incNumber = item.incident_number || item.number;
                        return (
                          <tr key={incNumber || idx}>
                            <td>
                              <strong>{incNumber || '—'}</strong>
                            </td>
                            <td>{item.short_description || item.description || '—'}</td>
                            <td>{item.impact || 'N/A'}</td>
                            <td>{item.status || 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // --- Main Render ---
  return (
    <div className="page-container">
      {isPermissionsLoading && (
        <div className="text-center mb-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Checking permissions...</p>
        </div>
      )}

      {!isPermissionsLoading && !canAccessProdPerformance() && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p className="mb-1">Your role does not have access to production performance.</p>
          <p className="mb-0">
            <strong>Current role:</strong> {getUserRole()}
          </p>
        </div>
      )}

      {!isPermissionsLoading && canAccessProdPerformance() && (
        <>
          <h2 className="kone-section-title mb-4">Prod Product Performance</h2>

          {/* Section 1: Time Period Filter */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Select Duration</h5>
            </div>
            <div className="card-body">
              <div className="row text-center align-items-center g-3">
                {TIME_RANGES.map((tr) => (
                  <div className="col-md-3 col-6" key={tr.value}>
                    <label
                      htmlFor={`duration-${tr.value}`}
                      className="d-inline-flex align-items-center justify-content-center gap-2 mb-0"
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        className="form-check-input mt-0"
                        type="radio"
                        name="prod-perf-duration"
                        id={`duration-${tr.value}`}
                        value={tr.value}
                        checked={timeRange === tr.value}
                        onChange={(e) => setTimeRange(e.target.value)}
                        style={{
                          width: '18px',
                          height: '18px',
                          flex: '0 0 auto',
                          borderRadius: '50%',
                        }}
                      />
                      <span>{tr.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Tribe Summary Cards */}
          {renderTribeSummaryCards()}

          {/* Section 3: Drill-Down */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Analyze Tribe or Service Offering</h5>
            </div>
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label htmlFor="drilldown-tribe" className="kone-label">
                    Select Tribe
                  </label>
                  <select
                    id="drilldown-tribe"
                    className="kone-select"
                    value={selectedTribe}
                    onChange={(e) => setSelectedTribe(e.target.value)}
                  >
                    <option value="">Select Tribe</option>
                    {TRIBES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <button
                    className="kone-btn kone-btn-secondary w-100"
                    onClick={() => handleAnalyzeTribe({ forceRefresh: true })}
                    disabled={isTribeAnalysisLoading || !selectedTribe || !timeRange}
                  >
                    {isTribeAnalysisLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze Tribe'
                    )}
                  </button>
                </div>
                <div className="col-md-3">
                  <label htmlFor="drilldown-offering" className="kone-label">
                    Select Service Offering
                  </label>
                  <select
                    id="drilldown-offering"
                    className="kone-select"
                    value={selectedOffering}
                    onChange={(e) => setSelectedOffering(e.target.value)}
                    disabled={!selectedTribe}
                  >
                    <option value="">Select offering...</option>
                    {offerings.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <button
                    className="kone-btn kone-btn-primary w-100"
                    onClick={() => handleAnalyze({ forceRefresh: true })}
                    disabled={
                      isDrillDownLoading || !selectedTribe || !selectedOffering || !timeRange
                    }
                  >
                    {isDrillDownLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze Service Offering'
                    )}
                  </button>
                </div>
              </div>
              <p className="text-muted mb-0 mt-3" style={{ fontSize: '0.85rem' }}>
                Use <strong>Analyze Tribe</strong> for a tribe-wide overview across all service
                offerings, or select a specific offering for detailed analysis.
              </p>
            </div>
          </div>

          {/* Tribe Analysis Error */}
          {tribeAnalysisError && (
            <div className="alert alert-danger" role="alert">
              {tribeAnalysisError}
            </div>
          )}

          {/* Tribe Analysis Loading */}
          {isTribeAnalysisLoading && (
            <div className="card mb-4">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-1">Analyzing tribe incidents...</p>
                <small className="text-muted">Fetching the latest data</small>
              </div>
            </div>
          )}

          {/* Tribe Analysis Detail */}
          {renderTribeAnalysisDetail()}

          {/* Drill-Down Error */}
          {drillDownError && (
            <div className="alert alert-danger" role="alert">
              {drillDownError}
            </div>
          )}

          {/* Drill-Down Loading */}
          {isDrillDownLoading && (
            <div className="card mb-4">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-1">Analyzing incidents...</p>
                <small className="text-muted">Fetching the latest data</small>
              </div>
            </div>
          )}

          {/* Drill-Down Detail */}
          {renderDrillDownDetail()}
        </>
      )}
    </div>
  );
}

export default ProdProductPerformance;
